<template>
  <div class="notifications-list h-full flex flex-col">
    <div
      v-if="enableFiltering && availableCategories.length > 1"
      class="kb-views px-3"
      role="tablist"
      aria-label="Category"
    >
      <button
        type="button"
        role="tab"
        :aria-selected="activeFilter === null"
        @click="setFilter(null)"
      >
        All
      </button>
      <button
        v-for="category in availableCategories"
        :key="category"
        type="button"
        role="tab"
        :aria-selected="activeFilter === category"
        @click="setFilter(category)"
      >
        {{ category }}
      </button>
    </div>

    <KbSkeletonRows
      v-if="loading"
      :rows="6"
      label="Loading notifications"
    />

    <div
      v-else-if="filteredNotifications.length === 0"
      class="py-8 text-center grid gap-2 justify-items-center"
    >
      <p class="text-[13px] text-kb-muted m-0">
        {{ activeFilter ? `No ${activeFilter} notifications.` : 'No notifications.' }}
      </p>
      <Button
        v-if="activeFilter"
        label="Show all"
        text
        size="small"
        @click="setFilter(null)"
      />
    </div>

    <div
      v-else
      ref="containerRef"
      :style="{ height: props.maxHeight === '100%' ? '100%' : 'auto', maxHeight: props.maxHeight === '100%' ? 'none' : (props.maxHeight || '400px') }"
      class="overflow-y-auto flex-1"
      @scroll="handleScroll"
    >
      <NotificationsNotificationItem
        v-for="notification in filteredNotifications"
        :key="notification.id"
        :notification="notification"
        :compact="compact"
        @notification-click="(notification) => {
          $emit('notificationClick', notification);
        }"
        @mark-as-read="(id) => markAsRead(id)"
        @delete="(id) => deleteNotification(id)"
      />

      <KbSpinner
        v-if="loadingMore"
        block
        size="md"
        text="loading more…"
        label="Loading more notifications"
      />
      <p
        v-else-if="!hasMore && filteredNotifications.length > 0"
        class="py-3 text-center font-mono text-[12px] text-kb-dim m-0"
      >
        that's all
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue' // Added computed import
import { useNotifications } from '~/composables/useNotifications'
import type { Notification } from '~/shared/types/notification'

const props = defineProps<{
  maxHeight?: string
  compact?: boolean
  enableFiltering?: boolean // New prop to enable category filtering
}>()

defineEmits<{
  notificationClick: [notification: Notification]
}>()

// Remove unreadCount from destructuring since we don't use it
const {
  notificationsArray,
  loadNotifications,
  loadMoreNotifications,
  markAsRead,
  deleteNotification,
  hasMore,
  loadingMore,
} = useNotifications()

const notifications = ref<Notification[]>([])
const loading = ref(true)
const containerRef = ref<HTMLElement | null>(null)
const activeFilter = ref<string | null>(null) // For category filtering

// Load initial notifications
onMounted(async () => {
  loading.value = true
  const initialNotifications = await loadNotifications()
  notifications.value = initialNotifications
  loading.value = false
})

// Implement infinite scroll
const handleScroll = async (event: Event) => {
  if (!hasMore.value || loadingMore.value) return

  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container

  // Load more when scrolled to bottom (with a 50px threshold)
  if (scrollHeight - scrollTop - clientHeight < 50) {
    const moreNotifications = await loadMoreNotifications()
    if (moreNotifications.length > 0) {
      notifications.value = [...notifications.value, ...moreNotifications]
    }
  }
}

// Filter notifications by category
const filteredNotifications = computed(() => {
  if (!activeFilter.value) return notifications.value
  return notifications.value.filter(notif => notif.category === activeFilter.value)
})

// Set active filter
const setFilter = (category: string | null) => {
  activeFilter.value = category
}

// Get unique categories from notifications
const availableCategories = computed(() => {
  const categories = new Set<string>()
  notifications.value.forEach((notif) => {
    if (notif.category) categories.add(notif.category)
  })
  return Array.from(categories)
})

// Watch for changes in notificationsArray from the store
watch(notificationsArray, (newNotifications) => {
  if (newNotifications) {
    notifications.value = newNotifications
  }
})
</script>
